import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  applyGanalyticsTracking,
  propagateQueryToExternalLinks,
  trackButtonActivity,
  urlIsExternal,
} from 'src/functions/propagateQueryParams';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';

import getButtonStyleByColorScheme, { ColorScheme } from 'app_previous/styles/functions/getButtonStyleByColorScheme';
import { StyledCaptionSpan, StyledCaptionSpanProps } from '../buttons/Button.primary.styles';
import { StyledLinkBrandedProps, StyledLinkBranded } from './Link.branded.styles';
import { StandardParagraphSize } from '../typography/paragraph.styles';
import { ButtonVariants } from 'src/api/models/Button';

export interface LinkBrandedProps extends StyledLinkBrandedProps, StyledCaptionSpanProps {
  text: string;
  caption?: string;
  url?: string;
  smooth?: boolean;
  rel?: string;
  id?: string;
  backgroundColor?: string;
  colorScheme?: ColorScheme;
  variant?: ButtonVariants[];
  ignoreCaption?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  style?: React.CSSProperties;
  className?: string;
  trackExperiment?: boolean;
}

const LinkBranded = (props: LinkBrandedProps): JSX.Element => {
  const {
    text,
    url,
    justify,
    caption,
    captionJustify,
    weight,
    smooth,
    rel,
    sizeSubLg,
    sizeMd,
    sizeXs,
    id,
    backgroundColor,
    colorScheme = 'Standard',
    variant = ['primary'],
    captionWeight,
    captionColor,
    noDefaultSizes,
    icon,
    iconPosition = 'left',
    style,
    className = '',
    textColor,
    standardSize,
  } = props;

  const size = StandardParagraphSize[standardSize]?.desktop || props.size;
  const sizeLg = StandardParagraphSize[standardSize]?.lg || props.sizeLg;
  const sizeSm = StandardParagraphSize[standardSize]?.sm || props.sizeSm;

  const sizeProps = {
    size,
    sizeLg,
    sizeSm,
    sizeMd,
    sizeSubLg,
    sizeXs,
  };

  const buttonRef = useRef<HTMLAnchorElement>();
  const mountedComponentRef = useRef(true);
  const [queryPropagatedUrl, updateHref] = useState(url);

  useEffect(() => {
    windowIsLoaded().then(() => {
      if (!mountedComponentRef.current) return null;

      updateHref(propagateQueryToExternalLinks(url));
    });
  }, [url]);

  useEffect(() => {
    return () => {
      mountedComponentRef.current = false;
    };
  }, []);

  const onClickToScroll = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    document.querySelector(url).scrollIntoView({
      behavior: 'smooth',
    });
  };

  // Smooth scroll only works when url is ID type
  const isScrollEvent = smooth && url.includes('#');
  const navigationProp = isScrollEvent ? {} : { href: queryPropagatedUrl || '#' };
  const targetProp = useMemo(() => {
    return urlIsExternal(url) ? { target: '_blank', rel: 'noreferrer' } : {};
  }, [url]);

  const colorSchemeVariant = variant?.length ? variant[0] : 'primary';
  const linkColors = useMemo(() => getButtonStyleByColorScheme(colorScheme, colorSchemeVariant), []);

  // Extend onClick listener for Trackers, etc.
  const trackOnClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const baseOnClick = isScrollEvent ? onClickToScroll : applyGanalyticsTracking(queryPropagatedUrl);
    baseOnClick(e);
    // >> Additional trackers
    trackButtonActivity(buttonRef.current, text);

    return true;
  };

  return (
    <StyledLinkBranded
      className={`styled-link-branded ${className}`}
      rel={rel}
      ref={buttonRef}
      justify={justify}
      weight={weight}
      backgroundColor={backgroundColor ?? linkColors.backgroundColor}
      textColor={textColor ?? linkColors.textColor}
      id={id}
      noDefaultSizes={noDefaultSizes}
      style={style}
      onClick={trackOnClick}
      {...sizeProps}
      {...navigationProp}
      {...targetProp}
    >
      <span>
        {icon && iconPosition === 'left' ? icon : null}
        {text}
        {icon && iconPosition === 'right' ? icon : null}
      </span>
      {caption ? (
        <StyledCaptionSpan
          className='_buttonCaption'
          captionJustify={captionJustify}
          captionWeight={captionWeight}
          captionColor={captionColor}
        >
          {caption}
        </StyledCaptionSpan>
      ) : null}
    </StyledLinkBranded>
  );
};

export const isUrlToFreeTrial = (url: string): boolean => {
  const domainPossibility = [
    'https://cohost.mn.co?plan=free-trial',
    'http://cohost.mn.co?plan=free-trial',
    'https://cohost.mn.co',
    'http://cohost.mn.co',
  ];
  const indexOfDomain = domainPossibility.findIndex((domain) => url.startsWith(domain));
  if (indexOfDomain > -1) {
    return true;
  }
  return false;
};

const LinkBrandedWrapper = (props: LinkBrandedProps): JSX.Element => {
  const ccText = 'No credit card required.';
  const captionSpecResult = (() => {
    if (!props.caption && isUrlToFreeTrial(props.url) && props.text !== 'Join Now' && !props.ignoreCaption) {
      return { caption: ccText, captionJustify: 'center' as StyledCaptionSpanProps['captionJustify'] };
    }

    return { caption: props.caption, captionJustify: props.captionJustify };
  })();

  return (
    <LinkBranded
      {...props}
      className={props.className}
      noDefaultSizes={props.noDefaultSizes || false}
      caption={captionSpecResult.caption}
      captionJustify={captionSpecResult.captionJustify}
      trackExperiment={props.trackExperiment}
    />
  );
};

export default LinkBrandedWrapper;
